import { type Ref, ref, unref, watch } from 'vue'
import { getMatrixRoomAvatarUrl } from '@/modules/chat-matrix-fast/services/matrixChatClient'
import { isSameAvatarAsset, preferStableAvatar } from '@/modules/chat-matrix-fast/utils/userDataExtractor'
import {
  avatarDebugLog,
  summarizeAvatarUrlForLog,
} from '@/modules/chat-matrix-fast/utils/chatDebugLog'

/** Persist group icons across dialog↔group switches so header/list don't remount-reload. */
const groupAvatarByRoomId = new Map<string, string>()

function rememberGroupAvatar(roomId: string | null | undefined, url: string | null | undefined) {
  const key = roomId?.trim()
  const value = url?.trim()
  if (!key || !value) {
    return
  }
  groupAvatarByRoomId.set(key, value)
}

function resolveSeedAvatar(
  iconValue: string | null | undefined,
  roomId: string | null | undefined,
): string | null {
  const cachedForRoom = roomId ? groupAvatarByRoomId.get(roomId) ?? null : null
  if (iconValue) {
    return preferStableAvatar(cachedForRoom, iconValue) || iconValue
  }
  return cachedForRoom
}

export function useGroupRoomAvatar(
  icon: Ref<string | null | undefined>,
  matrixRoomId: Ref<string | null | undefined>,
) {
  // Sync seed from list/store icon so header never starts with null→letter→image blink.
  const avatarUrl = ref<string | null>(
    resolveSeedAvatar(unref(icon), unref(matrixRoomId)),
  )
  let fetchToken = 0
  let lastRoomId: string | null = unref(matrixRoomId) ?? null

  watch(
    [icon, matrixRoomId],
    async ([iconValue, roomId]) => {
      const token = ++fetchToken
      const roomChanged = (roomId ?? null) !== lastRoomId
      const prevRoomId = lastRoomId
      lastRoomId = roomId ?? null
      const startedAt = performance.now()
      const cachedForRoom = roomId ? groupAvatarByRoomId.get(roomId) ?? null : null

      if (roomChanged) {
        if (roomId && cachedForRoom) {
          if (!avatarUrl.value || !isSameAvatarAsset(avatarUrl.value, cachedForRoom)) {
            avatarDebugLog('groupRoom · restore cache', {
              roomId,
              to: summarizeAvatarUrlForLog(cachedForRoom),
            })
            avatarUrl.value = cachedForRoom
          }
        } else if (!iconValue) {
          avatarDebugLog('groupRoom · reset (new room, no cache, no icon)', { roomId })
          avatarUrl.value = null
        }
      }

      if (iconValue) {
        // Тот же S3-файл с новой подписью — не трогаем src, иначе VAvatar мигает.
        if (avatarUrl.value && isSameAvatarAsset(avatarUrl.value, iconValue)) {
          rememberGroupAvatar(roomId, avatarUrl.value)
          return
        }
        const stableFromCache = preferStableAvatar(cachedForRoom, iconValue) || iconValue
        const next = roomChanged
          ? stableFromCache
          : (preferStableAvatar(avatarUrl.value, stableFromCache) || stableFromCache)
        avatarDebugLog('groupRoom · set from icon', {
          roomChanged,
          roomId: roomId ?? null,
          prevRoomId,
          from: summarizeAvatarUrlForLog(avatarUrl.value),
          icon: summarizeAvatarUrlForLog(iconValue),
          cached: summarizeAvatarUrlForLog(cachedForRoom),
          to: summarizeAvatarUrlForLog(next),
        })
        avatarUrl.value = next
        rememberGroupAvatar(roomId, next)
        return
      }

      if (!roomId) {
        // Не обнуляем: header переключится на contact.avatar, а при возврате в группу
        avatarDebugLog('groupRoom · keep (no room)', {
          prevRoomId,
          kept: summarizeAvatarUrlForLog(avatarUrl.value),
        })
        return
      }

      // Пока грузим Matrix avatar — оставляем предыдущую/кэш иконку.
      avatarDebugLog('groupRoom · fetch matrix', {
        roomId,
        roomChanged,
        kept: summarizeAvatarUrlForLog(avatarUrl.value),
      })
      const url = await getMatrixRoomAvatarUrl(roomId)
      if (token !== fetchToken) {
        avatarDebugLog('groupRoom · stale token', { roomId })
        return
      }
      if (url) {
        const next = preferStableAvatar(avatarUrl.value, url) || url
        avatarDebugLog('groupRoom · matrix ready', {
          roomId,
          to: summarizeAvatarUrlForLog(next),
          ms: Math.round(performance.now() - startedAt),
        })
        avatarUrl.value = next
        rememberGroupAvatar(roomId, next)
      }
    },
    { immediate: true },
  )

  return avatarUrl
}
